import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'
import ChartTableSwitch from './ChartTableSwitch'

const { Title } = Typography

// Compact label for the chart x-axis (e.g. "Jun 25") to keep month labels
// from overlapping, matching the other dashboard charts.
function formatMonthShort(record) {
  const date = new Date(record.year, record.month - 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

export default function PreAuthorizationStats({ stats }) {
  const [viewMode, setViewMode] = useState('chart')


  const COLORS = {
    numberOfPatients: '#cbd5e1',
    authorized: '#22c55e',
  }

  function formatMonth(record) {
    const date = new Date(record.year, record.month - 1)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  const columns = [
    {
      key: 'month',
      title: 'Month',
      render: (_, record) => formatMonth(record),
    },
    {
      key: 'count',
      title: '# of Patients',
      dataIndex: 'count',
      render: value => value.toLocaleString(),
    },
    {
      key: 'authorizedCount',
      title: 'Authorized',
      dataIndex: 'authorizedCount',
      render: value => value.toLocaleString(),
    },
    {
      key: 'percentage',
      title: 'Authorized %',
      render: (_, record, index) => {
        const pct = record.count ? Math.round((record.authorizedCount / record.count) * 100) : 0
        const prev = stats[index - 1]
        let prevPct = null
        if (prev) {
          prevPct = prev.count ? Math.round((prev.authorizedCount / prev.count) * 100) : 0
        }
        const diff = prevPct !== null ? pct - prevPct : null

        return (
          <span>
            {pct}%
            {diff !== null && diff !== 0 && (
              <span style={{ color: diff > 0 ? '#16a34a' : '#dc2626', marginLeft: 4 }}>
                ({diff > 0 ? '+' : ''}{diff}%)
              </span>
            )}
          </span>
        )
      },
    },
  ]

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
      zoom: { enabled: false },
      stacked: true,
      fontFamily: 'inherit',
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 0,
      },
    },
    states: {
      normal: { filter: { type: 'none' } },
    },
    stroke: { show: false },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      fontWeight: 500,
      labels: { colors: '#64748b' },
      markers: { radius: 3, fillColors: [COLORS.numberOfPatients, COLORS.authorized] },
    },
    xaxis: {
      categories: stats.map(s => formatMonthShort(s)),
      axisBorder: { show: false },
      axisTicks: { show: false },
      type: 'category',
      tickPlacement: 'on',
      labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        formatter(value) {
          return Math.round(value).toLocaleString()
        },
        style: { colors: '#94a3b8', fontSize: '11px' },
      },
      min: 0,
    },
    colors: [COLORS.authorized, COLORS.numberOfPatients],
    tooltip: {
      shared: true,
      intersect: false,
      marker: { fillColors: [COLORS.numberOfPatients, COLORS.authorized] },
      y: {
        formatter(value, { seriesIndex, dataPointIndex }) {
          const s = stats[dataPointIndex]
          if (seriesIndex === 1) {
            const pct = s.count ? Math.round((s.authorizedCount / s.count) * 100) : 0
            const prev = stats[dataPointIndex - 1]
            let diffHtml = ''
            if (prev) {
              const prevPct = prev.count ? Math.round((prev.authorizedCount / prev.count) * 100) : 0
              const diff = pct - prevPct
              if (diff !== 0) {
                const color = diff > 0 ? '#16a34a' : '#dc2626'
                const sign = diff > 0 ? '+' : ''
                diffHtml = ` <span style="color:${color}">(${sign}${diff}%)</span>`
              }
            }
            return `${s.authorizedCount.toLocaleString()} (${pct}%)${diffHtml}`
          }
          if (seriesIndex === 0) {
            return s.count.toLocaleString()
          }
          return Math.round(value).toLocaleString()
        },
      }
    }
}), [COLORS.authorized, COLORS.numberOfPatients, stats])

  // Flipped on purpose to show authorized in green and non-authorized in blue
  const chartSeries = [
    {
      name: '# of Patients',
      data: stats.map(s => s.authorizedCount),
    },
    {
      name: 'Authorized',
      data: stats.map(s => s.count - s.authorizedCount),
    },
  ]

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {/* level 2 for heading order; the inline style pins the visual size */}
          <Title
            level={2}
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: '#0f172a',
              letterSpacing: '-0.005em',
            }}
          >
            Pre-Authorization stats
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      <div>
        {viewMode === 'chart' && (
          <Row gutter={[0, 50]}>
            <Col span={24}>
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={350}
              />
            </Col>
          </Row>
        )}
        {viewMode === 'table' && (
          <Table
            columns={columns}
            dataSource={stats}
            rowKey={(record) => `${record.year}-${record.month}`}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        )}
      </div>
    </div>
  )
}

PreAuthorizationStats.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      authorizedCount: PropTypes.number.isRequired,
    }),
  ).isRequired,
}
